import React, { useCallback, useEffect, useMemo, useReducer, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Flex,
  Heading,
  IconButton,
  Input,
  Link,
  Menu,
  Spinner,
  Table,
  Tag,
  Text,
  VStack,
} from '@chakra-ui/react';
import {
  LuCalendarPlus,
  LuChevronDown,
  LuChevronRight,
  LuChevronUp,
  LuCircleCheck,
  LuExternalLink,
  LuSave,
  LuTrash2,
  LuX,
} from 'react-icons/lu';
import { Link as RouterLink, useLocation, useParams } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import axios from 'axios';
import {
  getNotableDateKeyForFiling,
  useNotableDates,
} from '../../hooks/useNotableDates';
import { useSavedSecFilingFiltersData } from '../../hooks/useSavedSecFilingFiltersData';
import { useSecFilingsData } from '../../hooks/useSecFilingsData';
import { isAfterDate } from '../../utils/stockReviewMode';
import { useStockReviewMode } from './StockReviewModeContext';
import { getLoginRedirectOptions, isRecoverableAuthError } from '../../utils/auth0Errors';

function getStockPriceTimelineHref(ticker) {
  const normalized = String(ticker || '').trim().toUpperCase();
  if (!normalized) return null;
  return `/stock/${normalized}/charts?dataset=stockPrice`;
}

const normaliseFormType = (value) => String(value || '').trim().toUpperCase();

const normalizeTrimmedStringList = (value) =>
  Array.isArray(value)
    ? value.flatMap((item) => {
        const trimmed = String(item || '').trim();
        return trimmed ? [trimmed] : [];
      })
    : [];

const pendingBooleanReducer = (state, value) => {
  const next = Boolean(value);
  return state === next ? state : next;
};

const matchesAny = (formType, patterns) => {
  const normalised = normaliseFormType(formType);

  return patterns.some((pattern) => {
    if (pattern instanceof RegExp) {
      return pattern.test(normalised);
    }

    return normalised === pattern;
  });
};

const insiderPatterns = [
  /^FORM\s*3(?:\/?A)?$/,
  /^FORM\s*4(?:\/?A)?$/,
  /^FORM\s*5(?:\/?A)?$/,
  /^3(?:\/?A)?$/,
  /^4(?:\/?A)?$/,
  /^5(?:\/?A)?$/,
];

const currentReportPatterns = [/^8-K(?:\/?A)?$/, /^6-K(?:\/?A)?$/];
const ipoRegistrationPatterns = [
  /^S-1(?:\/?A)?$/,
  /^424B4$/,
  /^DRS(?:\/?A)?$/,
];

export const SEC_FILING_FILTER_OPTIONS = [
  {
    value: 'all',
    label: 'All',
    predicate: () => true,
  },
  {
    value: 'exclude-insider',
    label: 'Exclude insider transactions',
    predicate: (filing) => !matchesAny(filing.formType, insiderPatterns),
  },
  {
    value: 'current-reports',
    label: 'Current reports',
    predicate: (filing) => matchesAny(filing.formType, currentReportPatterns),
  },
  {
    value: 'ipo-registration',
    label: 'IPO & registration filings',
    predicate: (filing) => matchesAny(filing.formType, ipoRegistrationPatterns),
  },
  {
    value: 'annual-quarterly',
    label: 'Annual & quarterly reports',
    predicate: (filing) =>
      matchesAny(filing.formType, [
        /^10-K.*$/,
        /^10-Q.*$/,
        /^20-F.*$/,
        /^40-F.*$/,
      ]),
  },
  {
    value: 'proxy-statements',
    label: 'Proxy statements',
    predicate: (filing) =>
      matchesAny(filing.formType, [
        'DEF 14A',
        'DEF14A',
        'DEFA14A',
        'DEFC14A',
        'DEFM14A',
        'DEFR14A',
        'DFAN14A',
        'PRE 14A',
        'PRE14A',
        'PREC14A',
        'PREM14A',
        'PRER14A',
      ]),
  },
  {
    value: 'insider-transactions',
    label: 'Insider transactions',
    predicate: (filing) => matchesAny(filing.formType, insiderPatterns),
  },
  {
    value: 'beneficial-ownership',
    label: 'Beneficial ownership',
    predicate: (filing) =>
      matchesAny(filing.formType, [
        /^SC\s*13[D,G].*$/,
        /^SC\s*13[D,G]A.*$/,
        /^13[D,G].*$/,
        /^13[D,G]A.*$/,
      ]),
  },
  {
    value: 'custom',
    label: 'Custom',
    predicate: () => true,
  },
];

const DEFAULT_CUSTOM_FILTER_CATEGORY_VALUES = new Set([
  'current-reports',
  'annual-quarterly',
  'proxy-statements',
  'insider-transactions',
  'beneficial-ownership',
]);

export const getSecFilingFilterOption = (value) =>
  SEC_FILING_FILTER_OPTIONS.find((option) => option.value === value) || SEC_FILING_FILTER_OPTIONS[0];

export const DEFAULT_SEC_FILING_SORT = Object.freeze({ column: 'filingDate', direction: 'desc' });

const FILINGS_FILTER_STORAGE_KEY = 'dcf_teacher_filings_filter';

const getPersistedState = () => {
  try {
    const raw = localStorage.getItem(FILINGS_FILTER_STORAGE_KEY);
    if (!raw) return { selectedFilter: 'exclude-insider', selectedCustomForms: [], savedFilterId: null };
    const parsed = JSON.parse(raw);
    // Handle old plain-string format
    if (typeof parsed === 'string') {
      const isValid = SEC_FILING_FILTER_OPTIONS.some((o) => o.value === parsed);
      return { selectedFilter: isValid ? parsed : 'exclude-insider', selectedCustomForms: [], savedFilterId: null };
    }
    const filter = parsed.filter;
    const isValid = SEC_FILING_FILTER_OPTIONS.some((o) => o.value === filter);
    return {
      selectedFilter: isValid ? filter : 'exclude-insider',
      selectedCustomForms: Array.isArray(parsed.forms) ? parsed.forms : [],
      savedFilterId: parsed.savedFilterId || null,
    };
  } catch {
    return { selectedFilter: 'exclude-insider', selectedCustomForms: [], savedFilterId: null };
  }
};

export const getSecFilingFilterFromSearch = (search = '') => {
  const requested = new URLSearchParams(search).get('filter');
  return SEC_FILING_FILTER_OPTIONS.some((option) => option.value === requested)
    ? requested
    : null;
};

const getInitialFilingsFilterState = (requestedFilter = null) => {
  const { selectedFilter, selectedCustomForms, savedFilterId } = getPersistedState();
  return {
    isFiltering: false,
    selectedFilter: requestedFilter || selectedFilter,
    selectedMasterTags: [],
    selectedCustomForms: requestedFilter ? [] : selectedCustomForms,
    activeSavedCustomFilterId: requestedFilter ? null : savedFilterId,
    currentReportsQuery: '',
    sortState: {
      ...DEFAULT_SEC_FILING_SORT,
      isDefault: true,
    },
  };
};

function filingsFilterReducer(state, action) {
  switch (action.type) {
    case 'SET_IS_FILTERING':
      return { ...state, isFiltering: action.value };
    case 'SET_SELECTED_FILTER':
      return { ...state, selectedFilter: action.value };
    case 'SET_SELECTED_MASTER_TAGS':
      return {
        ...state,
        selectedMasterTags: typeof action.value === 'function'
          ? action.value(state.selectedMasterTags)
          : action.value,
      };
    case 'SET_SELECTED_CUSTOM_FORMS':
      return {
        ...state,
        selectedCustomForms: typeof action.value === 'function'
          ? action.value(state.selectedCustomForms)
          : action.value,
      };
    case 'SET_ACTIVE_SAVED_CUSTOM_FILTER_ID':
      if ((action.value || null) === state.activeSavedCustomFilterId) {
        return state;
      }
      return {
        ...state,
        activeSavedCustomFilterId: action.value || null,
      };
    case 'SET_CURRENT_REPORTS_QUERY':
      return { ...state, currentReportsQuery: action.value };
    case 'SET_SORT_STATE':
      return {
        ...state,
        sortState: typeof action.value === 'function'
          ? action.value(state.sortState)
          : action.value,
      };
    case 'APPLY_FILTER_SELECT':
      return {
        ...state,
        isFiltering: true,
        selectedFilter: action.filterValue,
        activeSavedCustomFilterId: null,
        selectedCustomForms: action.filterValue !== 'custom' ? [] : state.selectedCustomForms,
      };
    case 'APPLY_SAVED_FILTER_SELECT':
      return {
        ...state,
        isFiltering: true,
        selectedFilter: 'custom',
        activeSavedCustomFilterId: action.savedFilterId || null,
        selectedCustomForms: action.forms,
      };
    default:
      return state;
  }
}

const compareDateValues = (a, b) => {
  if (a === b) {
    return 0;
  }

  if (a == null) {
    return 1;
  }

  if (b == null) {
    return -1;
  }

  return a < b ? -1 : 1;
};

const compareStringValues = (a, b) => {
  if (a === b) {
    return 0;
  }

  return a < b ? -1 : 1;
};

const MASTER_TAG_DEFS = Object.freeze({
  dividend: { label: 'Dividend', colorPalette: 'green' },
  financial_results: { label: 'Financial Results', colorPalette: 'cyan' },
  guidance: { label: 'Guidance', colorPalette: 'teal' },
  capital_raise: { label: 'Capital Raise/Debt', colorPalette: 'yellow' },
  m_and_a: { label: 'M&A', colorPalette: 'orange' },
  leadership_change: { label: 'Leadership/Board', colorPalette: 'pink' },
  shareholder_meeting: { label: 'Shareholder Meeting', colorPalette: 'blue' },
  buyback: { label: 'Buyback', colorPalette: 'lime' },
  litigation_regulatory: { label: 'Litigation/Regulatory', colorPalette: 'red' },
  restructuring: { label: 'Restructuring', colorPalette: 'gray' },
  other: { label: 'Other', colorPalette: 'gray' },
});

const MASTER_TAG_KEYS = Object.freeze(Object.keys(MASTER_TAG_DEFS));
const MAX_CUSTOM_FILTER_NAME_LENGTH = 40;

const isMissingRefreshTokenError = (error) => {
  const message = String(
    error?.message || error?.error_description || error?.error || ''
  ).toLowerCase();

  return (
    error?.error === 'missing_refresh_token' ||
    message.includes('missing refresh token') ||
    message.includes('missing_refresh_token')
  );
};

export const FilingsTable = React.memo(function FilingsTable({
  activeSort,
  columns,
  cycleSortForColumn,
  filings,
  hasFilings,
  isLoading,
  onClearReviewMode,
  reviewFilingsCount,
  reviewSinceDate,
  sortedFilings,
  isRowExpandable,
  hasMore,
  isLoadingAll,
  isLoadingMore,
  onLoadAll,
  onLoadMore,
  totalCount,
  onExpandRow,
  renderExpandedRow,
}) {
  const [page, setPage] = useState(0);
  const pageSize = 50;
  const resultKey = sortedFilings.map((filing) => filing.id).join('|');
  // Reset for changed filters/sorts, but preserve the page as summaries arrive.
  useEffect(() => { setPage(0); }, [resultKey]);
  const pageCount = Math.max(1, Math.ceil(sortedFilings.length / pageSize));
  const currentPage = Math.min(page, pageCount - 1);
  const visibleFilings = sortedFilings.slice(currentPage * pageSize, (currentPage + 1) * pageSize);
  const pagination = sortedFilings.length > pageSize ? (
    <Flex align="center" justify="center" gap={3} wrap="wrap">
      <Button size="sm" variant="outline" disabled={currentPage === 0}
        onClick={() => setPage(currentPage - 1)}>Previous page</Button>
      <Text fontSize="sm">
        Filings {currentPage * pageSize + 1}–{Math.min((currentPage + 1) * pageSize, sortedFilings.length)} of {sortedFilings.length} matches · Page {currentPage + 1} of {pageCount}
      </Text>
      <Button size="sm" variant="outline" disabled={currentPage + 1 >= pageCount}
        onClick={() => setPage(currentPage + 1)}>Next page</Button>
    </Flex>
  ) : null;
  const [expandedRowId, setExpandedRowId] = useState(null);
  const requestedExpandedDetailRef = React.useRef(null);
  const hasExpandableRows = Boolean(isRowExpandable && renderExpandedRow);

  const toggleExpandedRow = useCallback((filing) => {
    const willExpand = expandedRowId !== filing.id;
    setExpandedRowId(willExpand ? filing.id : null);
    if (willExpand && onExpandRow) {
      requestedExpandedDetailRef.current = `${filing.id}:${filing.form4?.status || 'none'}`;
      onExpandRow(filing);
    } else if (!willExpand) {
      requestedExpandedDetailRef.current = null;
    }
  }, [expandedRowId, onExpandRow]);

  useEffect(() => {
    if (!expandedRowId || !onExpandRow) return;
    const filing = sortedFilings.find((candidate) => candidate.id === expandedRowId);
    if (!filing || filing.form4?.status !== 'complete') return;
    const hasDetails = Boolean(
      filing.form4?.details_loaded ||
      Array.isArray(filing.form4?.transactions) ||
      Array.isArray(filing.form4?.reporting_owners)
    );
    const requestKey = `${filing.id}:${filing.form4.status}`;
    if (
      !hasDetails &&
      !filing.form4?.details_loading &&
      requestedExpandedDetailRef.current !== requestKey
    ) {
      requestedExpandedDetailRef.current = requestKey;
      onExpandRow(filing);
    }
  }, [expandedRowId, onExpandRow, sortedFilings]);

  return (
    <VStack align="stretch" gap={3}>
      {!isLoading && pagination}
      <Box borderWidth="1px" borderRadius="lg" overflow="hidden">
      {isLoading ? (
        <Flex align="center" justify="center" minH="200px">
          <Spinner size="lg" />
        </Flex>
      ) : hasFilings ? (
        <Table.Root variant="simple" size="sm">
          <Table.Header bg="gray.50">
            <Table.Row>
              {hasExpandableRows && (
                <Table.ColumnHeader width="44px" aria-label="Expand details" />
              )}
              {columns.map((column) => {
                const isSortedColumn =
                  column.sortable && activeSort.column === column.key;
                const isSortable = Boolean(column.sortable && cycleSortForColumn);
                const sortDirection = isSortedColumn
                  ? activeSort.direction
                  : null;
                const sortIcon = isSortable && isSortedColumn ? (
                  sortDirection === 'desc' ? (
                    <LuChevronDown />
                  ) : (
                    <LuChevronUp />
                  )
                ) : null;

                return (
                  <Table.ColumnHeader
                    key={column.key}
                    cursor={isSortable ? 'pointer' : 'default'}
                    onClick={() => {
                      if (isSortable) {
                        cycleSortForColumn(column.key, column.sortable);
                      }
                    }}
                    userSelect="none"
                    scope="col"
                    aria-sort={
                      isSortable
                        ? isSortedColumn
                          ? sortDirection === 'desc'
                            ? 'descending'
                            : 'ascending'
                          : 'none'
                        : 'none'
                    }
                  >
                    <Flex align="center" gap={1}>
                      <Text as="span">{column.label}</Text>
                      {sortIcon}
                    </Flex>
                  </Table.ColumnHeader>
                );
              })}
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {visibleFilings.map((filing, idx) => {
              const expandable = Boolean(isRowExpandable?.(filing));
              const isExpanded = expandable && expandedRowId === filing.id;
              const rowBackground = idx % 2 === 0 ? 'white' : 'gray.100';
              return (
                <React.Fragment key={filing.id}>
                  <Table.Row
                    borderBottomWidth="1px"
                    borderBottomColor="gray.100"
                    bg={rowBackground}
                    transition="background-color 120ms ease"
                    _hover={{ bg: 'blue.50' }}
                  >
                    {hasExpandableRows && (
                      <Table.Cell width="44px" px={2}>
                        {expandable && (
                          <IconButton
                            aria-label={
                              `${isExpanded ? 'Hide' : 'Show'} Form 4 details for `
                              + `${filing.form4?.row_summary?.owner_name || filing.formType}`
                            }
                            aria-expanded={isExpanded}
                            variant="ghost"
                            size="xs"
                            onClick={() => toggleExpandedRow(filing)}
                          >
                            {isExpanded ? <LuChevronDown /> : <LuChevronRight />}
                          </IconButton>
                        )}
                      </Table.Cell>
                    )}
                    {columns.map((column) => (
                      <Table.Cell key={column.key}>{column.render(filing)}</Table.Cell>
                    ))}
                  </Table.Row>
                  {isExpanded && (
                    <Table.Row bg="blue.50">
                      <Table.Cell colSpan={columns.length + 1} p={0}>
                        {renderExpandedRow(filing)}
                      </Table.Cell>
                    </Table.Row>
                  )}
                </React.Fragment>
              );
            })}
          </Table.Body>
        </Table.Root>
      ) : (
        <Box p={6} textAlign="center">
          <Text color="gray.600">
            {reviewSinceDate && filings.length > 0 && reviewFilingsCount === 0
              ? `No SEC filings were filed after ${reviewSinceDate}. Clear or change the review date to see earlier filings.`
              : filings.length === 0
              ? 'No SEC filings were found for this ticker.'
              : 'No SEC filings match this filter.'}
          </Text>
          {reviewSinceDate && filings.length > 0 && reviewFilingsCount === 0 && onClearReviewMode ? (
            <Button
              type="button"
              size="sm"
              mt={3}
              colorPalette="blue"
              variant="outline"
              onClick={onClearReviewMode}
            >
              Show all filings
            </Button>
          ) : null}
        </Box>
      )}
      </Box>
      {!isLoading && pagination}
      {hasMore && !isLoading ? (
        <Flex justify="center" gap={2} py={2} wrap="wrap">
          <Button
            type="button"
            variant="outline"
            colorPalette="blue"
            disabled={isLoadingMore}
            onClick={onLoadMore}
          >
            Load next 100 ({filings.length} of {totalCount})
          </Button>
          <Button
            type="button"
            colorPalette="blue"
            loading={isLoadingAll}
            loadingText={`Loading all filings (${filings.length} of ${totalCount})`}
            disabled={isLoadingMore && !isLoadingAll}
            onClick={onLoadAll}
          >
            Load all {totalCount} filings
          </Button>
        </Flex>
      ) : null}
      {reviewSinceDate && !isLoading && hasFilings ? (
        <Box
          borderWidth="1px"
          borderColor="blue.200"
          borderRadius="md"
          bg="blue.50"
          px={4}
          py={3}
          data-testid="review-filings-footer"
        >
          <Flex
            direction={{ base: 'column', sm: 'row' }}
            align={{ base: 'stretch', sm: 'center' }}
            justify="space-between"
            gap={3}
          >
            <Text fontSize="sm" color="gray.700">
              Showing only SEC filings since {reviewSinceDate} because review mode is on.
            </Text>
            {onClearReviewMode ? (
              <Button
                type="button"
                size="sm"
                colorPalette="blue"
                variant="outline"
                flexShrink={0}
                onClick={onClearReviewMode}
              >
                Show all filings
              </Button>
            ) : null}
          </Flex>
        </Box>
      ) : null}
    </VStack>
  );
});

function FilingNotableDateButton({
  filing,
  isSaving,
  notableDate,
  onDeleteNotableDate,
  onSaveNotableDate,
  stockPriceHref,
}) {
  if (!filing?.filingDate) {
    return (
      <Text fontSize="sm" color="gray.500">
        —
      </Text>
    );
  }

  if (notableDate) {
    return (
      <Flex alignItems="center" gap={1} justifyContent="flex-start">
        {stockPriceHref ? (
          <Button
            as={RouterLink}
            to={stockPriceHref}
            size="xs"
            variant="subtle"
            colorPalette="green"
            aria-label={`View on stock price chart for ${filing.formType || 'filing'} filed ${filing.filingDate}`}
            px={2}
          >
            <LuCircleCheck />
            View
          </Button>
        ) : (
          <Flex
            alignItems="center"
            gap={1}
            color="green.600"
            fontSize="sm"
            fontWeight="semibold"
            whiteSpace="nowrap"
          >
            <LuCircleCheck />
            <Text as="span">View</Text>
          </Flex>
        )}
        <Button
          size="xs"
          variant="ghost"
          colorPalette="red"
          aria-label={`Remove from timeline for ${filing.formType || 'filing'} filed ${filing.filingDate}`}
          disabled={isSaving}
          onClick={() => onDeleteNotableDate(notableDate, filing)}
          px={2}
        >
          <LuX />
          Remove
        </Button>
      </Flex>
    );
  }

  return (
    <Button
      size="xs"
      variant="outline"
      aria-label={`Add to timeline for ${filing.formType || 'filing'} filed ${filing.filingDate}`}
      disabled={isSaving}
      onClick={() => onSaveNotableDate(filing)}
      px={2}
    >
      <LuCalendarPlus />
      Add
    </Button>
  );
}

const FilingsControls = React.memo(function FilingsControls({
  activeFilter,
  activeFilterTotalCount,
  activeSavedCustomFilter,
  customFilterMenuLabel,
  customFilterNameInput,
  customFormOptions,
  currentReportsQuery,
  currentReportsQueryTrimmed,
  deleteActiveCustomFilter,
  isAuthLoading,
  isAuthenticated,
  isCustomFilter,
  isLoading,
  isSavingFilter,
  isSearchActive,
  masterTagMenuLabel,
  onClearCurrentReportsQuery,
  onCurrentReportsQueryChange,
  onCustomFilterNameChange,
  onFilterSelect,
  onSavedFilterSelect,
  onSelectAllCustomForms,
  onSelectCustomForm,
  onSelectNoCustomForms,
  saveCurrentCustomFilter,
  savedCustomFilterLimit,
  savedCustomFilters,
  saveFilterError,
  selectedCustomForms,
  selectedCustomFormSet,
  selectedMasterTagSet,
  setSelectedMasterTags,
  showMasterTagControls,
  sortedFilings,
  toggleMasterTag,
}) {
  // True when the user is viewing one of their named saved filters (not the generic Custom)
  const isViewingSavedFilter = Boolean(activeSavedCustomFilter);

  return (
    <>
      <Flex justify="space-between" align="center" gap={2} flexWrap="wrap">
        <Flex justify="flex-start" gap={2} flexWrap="wrap">
          <Menu.Root>
            <Menu.Trigger asChild>
              <Button size="sm" variant="outline">
                <Flex align="center" gap={2}>
                  {isViewingSavedFilter
                    ? activeSavedCustomFilter.name
                    : activeFilter?.label || 'Filter'}
                  <LuChevronDown size={16} />
                </Flex>
              </Button>
            </Menu.Trigger>
            <Menu.Positioner>
              <Menu.Content>
                {SEC_FILING_FILTER_OPTIONS.map((option) => (
                  <Menu.Item
                    key={option.value}
                    onClick={() => onFilterSelect(option)}
                    fontWeight={
                      !isViewingSavedFilter && option.value === activeFilter?.value
                        ? 'semibold'
                        : 'normal'
                    }
                    cursor="pointer"
                    _hover={{ bg: 'gray.100' }}
                  >
                    {option.label}
                  </Menu.Item>
                ))}
                {savedCustomFilters.length > 0 && (
                  <Menu.Item disabled fontWeight="semibold" color="gray.500">
                    Saved filters
                  </Menu.Item>
                )}
                {savedCustomFilters.map((saved) => (
                  <Menu.Item
                    key={`saved-filter-${saved.id}`}
                    onClick={() => onSavedFilterSelect(saved)}
                    fontWeight={
                      activeSavedCustomFilter?.id === saved.id ? 'semibold' : 'normal'
                    }
                    cursor="pointer"
                    _hover={{ bg: 'gray.100' }}
                  >
                    {saved.name}
                  </Menu.Item>
                ))}
              </Menu.Content>
            </Menu.Positioner>
          </Menu.Root>

          {isCustomFilter && (
            <>
              {/* Forms picker — shown for both saved and generic custom */}
              <Menu.Root closeOnSelect={false}>
                <Menu.Trigger asChild>
                  <Button size="sm" variant="outline">
                    <Flex align="center" gap={2}>
                      {customFilterMenuLabel}
                      <LuChevronDown size={16} />
                    </Flex>
                  </Button>
                </Menu.Trigger>
                <Menu.Positioner>
                  <Menu.Content maxH="320px" overflowY="auto">
                    <Menu.Item
                      onClick={onSelectAllCustomForms}
                      cursor="pointer"
                      _hover={{ bg: 'gray.100' }}
                      fontWeight="semibold"
                    >
                      Select all
                    </Menu.Item>
                    <Menu.Item
                      onClick={onSelectNoCustomForms}
                      cursor="pointer"
                      _hover={{ bg: 'gray.100' }}
                      fontWeight="semibold"
                    >
                      Clear all
                    </Menu.Item>
                    {customFormOptions.map((option) => {
                      const isSelected = selectedCustomFormSet.has(option.value);
                      return (
                        <Menu.Item
                          key={option.value}
                          onClick={() => onSelectCustomForm(option.value)}
                          cursor="pointer"
                          _hover={{ bg: 'gray.100' }}
                        >
                          <Flex w="full" align="center" justify="space-between" gap={3}>
                            <Text>{option.label}</Text>
                            <Text as="span" color="gray.600" minW="1.25rem" textAlign="right">
                              {isSelected ? '✓' : ''}
                            </Text>
                          </Flex>
                        </Menu.Item>
                      );
                    })}
                  </Menu.Content>
                </Menu.Positioner>
              </Menu.Root>

              {isViewingSavedFilter && (
                <IconButton
                  size="sm"
                  variant="outline"
                  colorPalette="red"
                  aria-label={`Delete saved filing filter ${activeSavedCustomFilter.name}`}
                  onClick={deleteActiveCustomFilter}
                  disabled={isSavingFilter || isAuthLoading}
                >
                  <LuTrash2 />
                </IconButton>
              )}
              <IconButton
                size="sm"
                variant="outline"
                aria-label="Save custom filing filter"
                onClick={saveCurrentCustomFilter}
                disabled={
                  isSavingFilter ||
                  isAuthLoading ||
                  selectedCustomForms.length === 0 ||
                  (isAuthenticated && customFilterNameInput.trim().length === 0)
                }
              >
                <LuSave />
              </IconButton>
              <Input
                size="sm"
                variant="outline"
                value={customFilterNameInput}
                onChange={(e) => onCustomFilterNameChange(e.target.value)}
                placeholder={`Name filter (max ${MAX_CUSTOM_FILTER_NAME_LENGTH})`}
                maxLength={MAX_CUSTOM_FILTER_NAME_LENGTH}
                maxW="230px"
                aria-label="Saved custom filter name"
              />
            </>
          )}

          {showMasterTagControls && (
            <Menu.Root closeOnSelect={false}>
              <Menu.Trigger asChild>
                <Button size="sm" variant="outline">
                  <Flex align="center" gap={2}>
                    {masterTagMenuLabel}
                    <LuChevronDown size={16} />
                  </Flex>
                </Button>
              </Menu.Trigger>
              <Menu.Positioner>
                <Menu.Content>
                  <Menu.Item
                    onClick={() => setSelectedMasterTags([])}
                    cursor="pointer"
                    _hover={{ bg: 'gray.100' }}
                    fontWeight="semibold"
                  >
                    Clear all
                  </Menu.Item>
                  {MASTER_TAG_KEYS.map((tagKey) => {
                    const def = MASTER_TAG_DEFS[tagKey];
                    const isSelected = selectedMasterTagSet.has(tagKey);
                    return (
                      <Menu.Item
                        key={tagKey}
                        onClick={() => toggleMasterTag(tagKey)}
                        cursor="pointer"
                        _hover={{ bg: 'gray.100' }}
                      >
                        <Flex w="full" align="center" justify="space-between" gap={3}>
                          <Flex align="center" gap={2}>
                            <Tag.Root size="sm" colorPalette={def?.colorPalette || 'gray'}>
                              <Tag.Label>{def?.label || tagKey}</Tag.Label>
                            </Tag.Root>
                          </Flex>
                          <Text as="span" color="gray.600" minW="1.25rem" textAlign="right">
                            {isSelected ? '✓' : ''}
                          </Text>
                        </Flex>
                      </Menu.Item>
                    );
                  })}
                </Menu.Content>
              </Menu.Positioner>
            </Menu.Root>
          )}

          <Box position="relative" maxW="280px" w="full">
            <Input
              size="sm"
              variant="outline"
              value={currentReportsQuery}
              onChange={(e) => onCurrentReportsQueryChange(e.target.value)}
              placeholder="Search filings..."
              pr={currentReportsQuery ? '2.25rem' : undefined}
              aria-label="Search filings"
            />
            {currentReportsQuery && (
              <IconButton
                aria-label="Clear search"
                variant="ghost"
                size="xs"
                position="absolute"
                right="1"
                top="50%"
                transform="translateY(-50%)"
                minW="auto"
                h="auto"
                p="1"
                onClick={onClearCurrentReportsQuery}
              >
                <LuX size={16} />
              </IconButton>
            )}
          </Box>
        </Flex>

        {!isLoading && (
          <Text fontSize="sm" color="gray.600">
            {isSearchActive
              ? `Showing ${sortedFilings.length} entries matching "${currentReportsQueryTrimmed}"`
              : `Showing ${sortedFilings.length} out of ${activeFilterTotalCount} entries`}
          </Text>
        )}
      </Flex>
      {isCustomFilter && !isViewingSavedFilter && (
        <Text fontSize="sm" color={saveFilterError ? 'red.600' : 'gray.600'}>
          {saveFilterError ||
            `Saved filters: ${savedCustomFilters.length}/${savedCustomFilterLimit} (${isAuthenticated ? 'account' : 'log in to save'}) • Name ${customFilterNameInput.length}/${MAX_CUSTOM_FILTER_NAME_LENGTH}`}
        </Text>
      )}
      {isViewingSavedFilter && isSavingFilter && (
        <Text fontSize="sm" color="gray.500">Saving…</Text>
      )}
    </>
  );
});

const isForm4Filing = (filing) => /^(?:FORM\s*)?4(?:\/A)?$/.test(
  normaliseFormType(filing?.formType)
);

const formatForm4Number = (value) => {
  const number = Number(value);
  if (!Number.isFinite(number)) return value || '—';
  return new Intl.NumberFormat('en-US', { maximumFractionDigits: 4 }).format(number);
};

const formatForm4Money = (value, maximumFractionDigits = 0) => {
  const number = Number(value);
  if (!Number.isFinite(number)) return null;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits,
  }).format(number);
};

const getActionColor = (action) => {
  const normalized = String(action || '').toLowerCase();
  if (normalized === 'buy') return 'green';
  if (normalized === 'sell') return 'red';
  if (normalized.includes('tax')) return 'orange';
  if (normalized.includes('grant')) return 'purple';
  return 'blue';
};

const getPrimaryInsiderHref = (form4) => {
  const owner = Array.isArray(form4?.reporting_owners)
    ? form4.reporting_owners.find((candidate) => candidate?.cik)
    : null;
  const cik = owner?.cik || form4?.row_summary?.owner_cik;
  return cik ? `/insider/${cik}` : null;
};

function Form4CompactSummary({ filing }) {
  const summary = filing.form4?.row_summary;
  const insiderHref = getPrimaryInsiderHref(filing.form4);
  if (filing.form4?.status === 'error') {
    return (
      <Text color="red.600" fontSize="sm">
        Form 4 unavailable — expand to retry
      </Text>
    );
  }
  if (filing.form4?.status === 'pending') {
    return (
      <Flex align="center" gap={2} color="gray.600">
        <Spinner size="xs" />
        <Text>Parsing insider filing…</Text>
      </Flex>
    );
  }
  if (filing.form4?.status !== 'complete' || !summary) {
    return <Text>{filing.description || '—'}</Text>;
  }

  const money = formatForm4Money(summary.total_value);
  return (
    <Flex align="center" gap={2} wrap="wrap">
      {insiderHref ? (
        <Link
          as={RouterLink}
          to={insiderHref}
          color="blue.600"
          fontWeight="semibold"
          onClick={(event) => event.stopPropagation()}
        >
          {summary.owner_name || 'Reporting owner'}
        </Link>
      ) : (
        <Text fontWeight="semibold">{summary.owner_name || 'Reporting owner'}</Text>
      )}
      {summary.title && (
        <Tag.Root size="sm" colorPalette="gray" variant="subtle">
          <Tag.Label>{summary.title}</Tag.Label>
        </Tag.Root>
      )}
      {summary.action && (
        <Tag.Root size="sm" colorPalette={getActionColor(summary.action)} variant="subtle">
          <Tag.Label>{summary.action}</Tag.Label>
        </Tag.Root>
      )}
      {summary.shares && (
        <Text fontSize="sm" color="gray.600">
          {formatForm4Number(summary.shares)} shares{money ? ` · ${money}` : ''}
        </Text>
      )}
      {summary.transaction_count > 1 && (
        <Text fontSize="xs" color="gray.500">
          First of {summary.transaction_count} transactions
        </Text>
      )}
    </Flex>
  );
}

function Form4ExpandedDetails({ filing }) {
  const form4 = filing.form4;
  const hasDetailedPayload = Boolean(
    form4?.details_loaded ||
    Array.isArray(form4?.transactions) ||
    Array.isArray(form4?.holdings) ||
    Array.isArray(form4?.reporting_owners)
  );
  if (form4?.details_error) {
    return (
      <Alert.Root status="error" m={4}>
        <Alert.Indicator />
        <Alert.Content>{form4.details_error}</Alert.Content>
      </Alert.Root>
    );
  }
  if (form4?.status === 'error' && !form4.details_loading) {
    return (
      <Alert.Root status="error" m={4}>
        <Alert.Indicator />
        <Alert.Content>{form4.error || 'Unable to load Form 4 details.'}</Alert.Content>
      </Alert.Root>
    );
  }
  if (
    !form4 ||
    form4.status === 'pending' ||
    form4.details_loading ||
    (form4.status === 'complete' && !hasDetailedPayload)
  ) {
    return (
      <Flex align="center" gap={2} p={5} color="gray.600">
        <Spinner size="sm" />
        <Text>Loading Form 4 details from the SEC…</Text>
      </Flex>
    );
  }

  const owners = Array.isArray(form4.reporting_owners) ? form4.reporting_owners : [];
  const transactions = Array.isArray(form4.transactions) ? form4.transactions : [];
  const holdings = Array.isArray(form4.holdings) ? form4.holdings : [];
  return (
    <VStack align="stretch" gap={4} p={{ base: 4, md: 5 }}>
      {owners.map((owner, index) => (
        <Flex key={owner.cik || owner.name || index} gap={2} align="center" wrap="wrap">
          {owner.cik ? (
            <Link
              as={RouterLink}
              to={`/insider/${owner.cik}`}
              color="blue.600"
              fontWeight="bold"
            >
              {owner.display_name || owner.name || 'Reporting owner'}
            </Link>
          ) : (
            <Text fontWeight="bold">{owner.display_name || owner.name || 'Reporting owner'}</Text>
          )}
          {owner.title_short && (
            <Tag.Root size="sm" colorPalette="gray" variant="subtle">
              <Tag.Label>{owner.title_short}</Tag.Label>
            </Tag.Root>
          )}
          {owner.officer_title && owner.officer_title !== owner.title_short && (
            <Text fontSize="sm" color="gray.600">{owner.officer_title}</Text>
          )}
          {owner.cik && <Text fontSize="xs" color="gray.500">CIK {owner.cik}</Text>}
        </Flex>
      ))}

      {form4.is_10b5_one && (
        <Tag.Root size="sm" colorPalette="blue" variant="subtle" alignSelf="flex-start">
          <Tag.Label>Rule 10b5-1 trading plan</Tag.Label>
        </Tag.Root>
      )}

      {transactions.map((transaction, index) => {
        const value = formatForm4Money(transaction.total_value);
        return (
          <Box
            key={`${transaction.transaction_date || 'transaction'}-${index}`}
            borderWidth="1px"
            borderColor="gray.200"
            borderRadius="md"
            bg="white"
            p={4}
          >
            <Flex gap={2} align="center" wrap="wrap" mb={2}>
              <Tag.Root
                size="sm"
                colorPalette={getActionColor(transaction.action)}
                variant="subtle"
              >
                <Tag.Label>{transaction.action || 'Transaction'}</Tag.Label>
              </Tag.Root>
              <Text fontWeight="semibold">{transaction.security_title || 'Security'}</Text>
              {transaction.transaction_date && (
                <Text fontSize="sm" color="gray.600">{transaction.transaction_date}</Text>
              )}
            </Flex>
            <Flex gap={{ base: 3, md: 6 }} wrap="wrap" fontSize="sm">
              <Text><strong>Shares:</strong> {formatForm4Number(transaction.shares)}</Text>
              {transaction.price_per_share && (
                <Text>
                  <strong>Price:</strong>{' '}
                  {formatForm4Money(transaction.price_per_share, 4)}
                </Text>
              )}
              {value && <Text><strong>Value:</strong> {value}</Text>}
              {transaction.shares_owned_after && (
                <Text>
                  <strong>Owned afterward:</strong>{' '}
                  {formatForm4Number(transaction.shares_owned_after)}
                </Text>
              )}
              <Text><strong>SEC code:</strong> {transaction.sec_code || '—'}</Text>
              {transaction.ownership_form && (
                <Text>
                  <strong>Ownership:</strong>{' '}
                  {transaction.ownership_form === 'D' ? 'Direct' : 'Indirect'}
                </Text>
              )}
              {transaction.ownership_nature && (
                <Text><strong>Ownership detail:</strong> {transaction.ownership_nature}</Text>
              )}
              {transaction.conversion_or_exercise_price && (
                <Text>
                  <strong>Exercise price:</strong>{' '}
                  {formatForm4Money(transaction.conversion_or_exercise_price, 4)}
                </Text>
              )}
              {transaction.exercise_date && (
                <Text><strong>Exercise date:</strong> {transaction.exercise_date}</Text>
              )}
              {transaction.expiration_date && (
                <Text><strong>Expiration date:</strong> {transaction.expiration_date}</Text>
              )}
            </Flex>

            {Array.isArray(transaction.footnotes) && transaction.footnotes.length > 0 && (
              <Box mt={3} pt={3} borderTopWidth="1px" borderTopColor="gray.200">
                <Text fontSize="sm" fontWeight="semibold" mb={1}>SEC footnotes</Text>
                <VStack align="stretch" gap={1}>
                  {transaction.footnotes.map((footnote) => (
                    <Text key={footnote.id} fontSize="sm" color="gray.700">
                      <Text as="span" fontWeight="semibold">{footnote.id}:</Text>{' '}
                      {footnote.text}
                    </Text>
                  ))}
                </VStack>
              </Box>
            )}
          </Box>
        );
      })}

      {holdings.length > 0 && (
        <Box>
          <Text fontWeight="semibold" mb={2}>Other reported holdings</Text>
          <VStack align="stretch" gap={2}>
            {holdings.map((holding, index) => (
              <Flex
                key={`${holding.security_title || 'holding'}-${index}`}
                borderWidth="1px"
                borderColor="gray.200"
                borderRadius="md"
                bg="white"
                p={3}
                gap={4}
                wrap="wrap"
                fontSize="sm"
              >
                <Text fontWeight="semibold">{holding.security_title || 'Security'}</Text>
                <Text>{formatForm4Number(holding.shares_owned)} shares</Text>
                {holding.ownership_form && (
                  <Text>{holding.ownership_form === 'D' ? 'Direct' : 'Indirect'}</Text>
                )}
                {holding.ownership_nature && <Text>{holding.ownership_nature}</Text>}
              </Flex>
            ))}
          </VStack>
        </Box>
      )}

      {form4.remarks && (
        <Box borderLeftWidth="3px" borderLeftColor="blue.300" pl={3}>
          <Text fontSize="sm" fontWeight="semibold">Remarks</Text>
          <Text fontSize="sm" color="gray.700">{form4.remarks}</Text>
        </Box>
      )}
    </VStack>
  );
}

export const SEC_FILING_COLUMNS = [
  {
    key: 'formType',
    label: 'Form type',
    sortable: true,
    render: (filing) =>
      filing.href ? (
        <Link
          href={filing.href}
          color="blue.500"
          isExternal
          display="inline-flex"
          alignItems="center"
          target="_blank"
          rel="noopener noreferrer"
          fontWeight="semibold"
        >
          {filing.formType || '—'}
          <LuExternalLink mx="2px" />
        </Link>
      ) : (
        <Text fontWeight="semibold">{filing.formType || '—'}</Text>
      ),
  },
  {
    key: 'description',
    label: 'Form description',
    sortable: false,
    render: (filing) => (
      <VStack align="flex-start" spacing={1}>
        {filing.ai?.masterTags?.length > 0 && (
          <Box>
            {filing.ai.masterTags.map((tag) => {
              const def = MASTER_TAG_DEFS[tag] || {
                label: tag,
                colorPalette: 'gray',
              };
              return (
                <Tag.Root
                  key={`master-${tag}`}
                  size="lg"
                  colorPalette={def.colorPalette}
                  mr={2}
                  mb={2}
                >
                  <Tag.Label fontWeight="semibold">{def.label}</Tag.Label>
                </Tag.Root>
              );
            })}
          </Box>
        )}
        {!String(filing.formTypeKey || '').startsWith('8-K') && (
          isForm4Filing(filing)
            ? <Form4CompactSummary filing={filing} />
            : <Text>{filing.description || '—'}</Text>
        )}
        {String(filing.formTypeKey || '').startsWith('8-K') ? (
          <Box
            pl={4}
            mt={1}
            borderLeftWidth="2px"
            borderLeftColor="gray.200"
            w="full"
          >
            <VStack align="flex-start" spacing={1}>
              {!filing.ai?.status && !filing.ai?.summary140 && (
                <Text fontSize="sm" color="gray.500">Queued for summary</Text>
              )}
              {filing.ai?.status === 'pending' && (
                <Tag.Root size="sm" colorPalette="blue">
                  <Tag.Label>Generating…</Tag.Label>
                </Tag.Root>
              )}
              {filing.ai?.status === 'error' && (
                <Tag.Root size="sm" colorPalette="red">
                  <Tag.Label>Summary unavailable</Tag.Label>
                </Tag.Root>
              )}
              {filing.ai?.summary140 && (
                <Text fontSize="sm" color="gray.600">
                  {filing.ai.summary140}
                </Text>
              )}
            </VStack>
          </Box>
        ) : (
          null
        )}
      </VStack>
    ),
  },
  {
    key: 'filingDate',
    label: 'Filing date',
    sortable: true,
    render: (filing) => filing.filingDate || '—',
  },
  {
    key: 'reportingDate',
    label: 'Reporting date',
    sortable: true,
    render: (filing) => filing.reportingDate || '—',
  },
];

function useSavedCustomFilingFilters({
  activeSavedCustomFilterId,
  apiBaseUrl,
  getAccessTokenSilently,
  isAuthenticated,
  onActiveSavedCustomFilterIdChange,
  removeSavedCustomFilter,
  savedCustomFilterLimit,
  savedCustomFilters,
  upsertSavedCustomFilter,
}) {
  const [customFilterNameDraft, setCustomFilterNameDraft] = useState(null);
  const [saveFilterError, setSaveFilterError] = useState('');
  const [isSavingFilter, setIsSavingFilter] = useReducer(pendingBooleanReducer, false);

  const canSaveMoreCustomFilters = savedCustomFilters.length < savedCustomFilterLimit;
  const activeSavedCustomFilter = useMemo(() => {
    if (!isAuthenticated) {
      return null;
    }

    const found = savedCustomFilters.find(
      (filter) => filter.id === activeSavedCustomFilterId
    );
    if (!found) {
      return null;
    }

    const forms = normalizeTrimmedStringList(found.forms);

    return { ...found, forms };
  }, [activeSavedCustomFilterId, isAuthenticated, savedCustomFilters]);

  const customFilterNameInput = customFilterNameDraft
    ?? String(activeSavedCustomFilter?.name || '').slice(
      0,
      MAX_CUSTOM_FILTER_NAME_LENGTH
    );

  const clearActiveCustomFilter = React.useCallback(() => {
    onActiveSavedCustomFilterIdChange(null);
    setCustomFilterNameDraft('');
  }, [onActiveSavedCustomFilterIdChange]);

  const selectSavedCustomFilter = React.useCallback((savedFilterId) => {
    onActiveSavedCustomFilterIdChange(savedFilterId);
    setCustomFilterNameDraft(null);
  }, [onActiveSavedCustomFilterIdChange]);

  const updateCustomFilterName = React.useCallback((value) => {
    setCustomFilterNameDraft(value.slice(0, MAX_CUSTOM_FILTER_NAME_LENGTH));
    setSaveFilterError((prev) => (prev ? '' : prev));
  }, []);

  const saveCurrentCustomFilter = React.useCallback(async (forms) => {
    setSaveFilterError('');

    if (isSavingFilter) {
      return false;
    }

    if (!isAuthenticated) {
      return 'auth-required';
    }

    const isEditingSavedFilter = Boolean(activeSavedCustomFilter);

    if (!isEditingSavedFilter && !canSaveMoreCustomFilters) {
      setSaveFilterError(`You can save up to ${savedCustomFilterLimit} filters.`);
      return false;
    }

    const formsToSave = normalizeTrimmedStringList(forms);
    if (formsToSave.length === 0) {
      setSaveFilterError('Select at least one form before saving.');
      return false;
    }

    const cleanedName = customFilterNameInput.trim();
    if (!cleanedName) {
      setSaveFilterError('Filter name is required.');
      return false;
    }
    if (cleanedName.length > MAX_CUSTOM_FILTER_NAME_LENGTH) {
      setSaveFilterError(`Filter name must be ${MAX_CUSTOM_FILTER_NAME_LENGTH} characters or fewer.`);
      return false;
    }

    setIsSavingFilter(true);
    try {
      const token = await getAccessTokenSilently({ detailedResponse: false });
      let saved = null;
      if (isEditingSavedFilter) {
        const endpoint = `${apiBaseUrl}/api/sec-filings/filters/${encodeURIComponent(
          activeSavedCustomFilter.id
        )}`;
        const { data } = await axios.put(
          endpoint,
          { name: cleanedName, forms: formsToSave },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        saved = data?.filter;
      } else {
        const endpoint = `${apiBaseUrl}/api/sec-filings/filters`;
        const { data } = await axios.post(
          endpoint,
          { name: cleanedName, forms: formsToSave },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        saved = data?.filter;
      }
      if (saved) {
        upsertSavedCustomFilter(saved);
        onActiveSavedCustomFilterIdChange(saved.id);
        setCustomFilterNameDraft(saved.name || cleanedName);
        return true;
      }
      return false;
    } catch (err) {
      if (isMissingRefreshTokenError(err)) {
        return 'auth-required';
      }

      const message =
        err?.response?.data?.error || err?.message || 'Could not save this custom filter.';
      setSaveFilterError(message);
      return false;
    } finally {
      setIsSavingFilter(false);
    }
  }, [
    activeSavedCustomFilter,
    apiBaseUrl,
    canSaveMoreCustomFilters,
    getAccessTokenSilently,
    isAuthenticated,
    isSavingFilter,
    customFilterNameInput,
    onActiveSavedCustomFilterIdChange,
    savedCustomFilterLimit,
    upsertSavedCustomFilter,
  ]);

  const deleteActiveCustomFilter = React.useCallback(async () => {
    setSaveFilterError('');

    if (isSavingFilter) {
      return false;
    }

    if (!activeSavedCustomFilter) {
      return false;
    }

    if (!isAuthenticated) {
      return 'auth-required';
    }

    const confirmed = window.confirm(
      `Delete saved filter "${activeSavedCustomFilter.name}"?`
    );
    if (!confirmed) {
      return false;
    }

    const deletedFilter = activeSavedCustomFilter;
    setIsSavingFilter(true);
    onActiveSavedCustomFilterIdChange(null);
    setCustomFilterNameDraft('');
    try {
      const token = await getAccessTokenSilently({ detailedResponse: false });
      const endpoint = `${apiBaseUrl}/api/sec-filings/filters/${encodeURIComponent(
        deletedFilter.id
      )}`;
      await axios.delete(endpoint, {
        headers: { Authorization: `Bearer ${token}` },
      });

      removeSavedCustomFilter(deletedFilter.id);
      // Clear localStorage so the deleted saved filter cannot be restored.
      try {
        localStorage.removeItem(FILINGS_FILTER_STORAGE_KEY);
      } catch { /* ignore */ }
      return true;
    } catch (err) {
      onActiveSavedCustomFilterIdChange(deletedFilter.id);
      setCustomFilterNameDraft(String(deletedFilter.name || '').slice(
        0,
        MAX_CUSTOM_FILTER_NAME_LENGTH
      ));

      if (isMissingRefreshTokenError(err)) {
        return 'auth-required';
      }

      const message =
        err?.response?.data?.error || err?.message || 'Could not delete this custom filter.';
      setSaveFilterError(message);
      return false;
    } finally {
      setIsSavingFilter(false);
    }
  }, [
    activeSavedCustomFilter,
    apiBaseUrl,
    getAccessTokenSilently,
    isAuthenticated,
    isSavingFilter,
    onActiveSavedCustomFilterIdChange,
    removeSavedCustomFilter,
  ]);

  const autosaveTimeoutRef = React.useRef(null);
  const scheduleSaveActiveCustomFilterForms = React.useCallback((forms) => {
    if (!activeSavedCustomFilter || !isAuthenticated) return;

    const formsToSave = normalizeTrimmedStringList(forms);
    if (formsToSave.length === 0) return;

    const savedForms = normalizeTrimmedStringList(activeSavedCustomFilter.forms);
    const sameLength = formsToSave.length === savedForms.length;
    const sameContent = sameLength && formsToSave.every((f) => savedForms.includes(f));
    if (sameContent) return;

    clearTimeout(autosaveTimeoutRef.current);
    autosaveTimeoutRef.current = setTimeout(async () => {
      try {
        const token = await getAccessTokenSilently({ detailedResponse: false });
        const endpoint = `${apiBaseUrl}/api/sec-filings/filters/${encodeURIComponent(activeSavedCustomFilter.id)}`;
        const { data } = await axios.put(
          endpoint,
          { name: activeSavedCustomFilter.name, forms: formsToSave },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const saved = data?.filter;
        if (saved) {
          upsertSavedCustomFilter(saved);
        }
        // Update localStorage with the latest forms
        try {
          localStorage.setItem(FILINGS_FILTER_STORAGE_KEY, JSON.stringify({
            filter: 'custom',
            forms: formsToSave,
            savedFilterId: activeSavedCustomFilter.id,
          }));
        } catch { /* ignore */ }
      } catch (err) {
        if (isMissingRefreshTokenError(err)) return; // silently ignore auth errors for autosave
      }
    }, 800);
  }, [
    activeSavedCustomFilter,
    apiBaseUrl,
    getAccessTokenSilently,
    isAuthenticated,
    upsertSavedCustomFilter,
  ]);

  useEffect(() => {
    return () => clearTimeout(autosaveTimeoutRef.current);
  }, [activeSavedCustomFilterId]);

  return {
    activeSavedCustomFilter,
    clearActiveCustomFilter,
    customFilterNameInput,
    deleteActiveCustomFilter,
    isSavingFilter,
    saveCurrentCustomFilter,
    saveFilterError,
    scheduleSaveActiveCustomFilterForms,
    selectSavedCustomFilter,
    updateCustomFilterName,
  };
}

const getFilingMasterTags = (filing) => {
  const masterTagsRaw = filing?.ai?.masterTags;
  return Array.isArray(masterTagsRaw) && masterTagsRaw.length > 0
    ? masterTagsRaw
    : ['other'];
};

const filingMatchesSearch = (filing, query) => {
  const masterTags = getFilingMasterTags(filing);
  const haystackParts = [];
  const haystackValues = [
    filing?.description,
    filing?.formType,
    filing?.ai?.summary140,
    ...(Array.isArray(filing?.sections) ? filing.sections : []),
    ...(Array.isArray(filing?.ai?.tags) ? filing.ai.tags : []),
    ...(Array.isArray(masterTags) ? masterTags : []),
  ];
  for (const value of haystackValues) {
    if (value) {
      haystackParts.push(String(value).toLowerCase());
    }
  }
  const haystack = haystackParts.join(' ');

  return haystack.includes(query);
};

const SORT_COMPARATORS = {
  formType: (a, b) => compareStringValues(a.formTypeKey, b.formTypeKey),
  filingDate: (a, b) => compareDateValues(a.filingDateValue, b.filingDateValue),
  reportingDate: (a, b) =>
    compareDateValues(a.reportingDateValue, b.reportingDateValue),
};

function useFilteredSecFilings({
  activeFilter,
  currentReportsQueryNormalised,
  filings,
  isCurrentReportsFilter,
  isCustomFilter,
  selectedCustomFormSet,
  selectedMasterTagSet,
  sortState,
}) {
  const filteredFilings = useMemo(() => {
    if (currentReportsQueryNormalised) {
      return filings.filter((filing) => {
        try {
          if (!matchesAny(filing.formType, currentReportPatterns)) {
            return false;
          }

          return filingMatchesSearch(filing, currentReportsQueryNormalised);
        } catch (err) {
          return true;
        }
      });
    }

    const predicate = activeFilter?.predicate || (() => true);
    return filings.filter((filing) => {
      try {
        if (!predicate(filing)) {
          return false;
        }

        if (!isCurrentReportsFilter) {
          if (isCustomFilter) {
            if (!selectedCustomFormSet.has(filing.formTypeKey)) {
              return false;
            }

            if (selectedMasterTagSet.size > 0) {
              const masterTags = getFilingMasterTags(filing);
              if (!masterTags.some((tag) => selectedMasterTagSet.has(tag))) {
                return false;
              }
            }
          }
        } else {
          const masterTags = getFilingMasterTags(filing);
          if (selectedMasterTagSet.size > 0) {
            const passesMasterTags = masterTags.some((tag) => selectedMasterTagSet.has(tag));
            if (!passesMasterTags) {
              return false;
            }
          }
        }

        return true;
      } catch (err) {
        return true;
      }
    });
  }, [
    activeFilter,
    filings,
    isCurrentReportsFilter,
    isCustomFilter,
    currentReportsQueryNormalised,
    selectedCustomFormSet,
    selectedMasterTagSet,
  ]);

  const activeFilterTotalCount = useMemo(() => {
    const predicate = activeFilter?.predicate || (() => true);

    return filings.reduce((count, filing) => {
      try {
        return predicate(filing) ? count + 1 : count;
      } catch (err) {
        return count + 1;
      }
    }, 0);
  }, [activeFilter, filings]);

  const activeSort = sortState;

  const sortedFilings = useMemo(() => {
    if (!filteredFilings.length) {
      return filteredFilings;
    }

    const comparator = SORT_COMPARATORS[activeSort.column];
    if (!comparator) {
      return filteredFilings;
    }

    const multiplier = activeSort.direction === 'desc' ? -1 : 1;
    return filteredFilings.toSorted((a, b) => comparator(a, b) * multiplier);
  }, [activeSort, filteredFilings]);

  return {
    activeFilterTotalCount,
    activeSort,
    filteredFilings,
    sortedFilings,
  };
}

function useCustomFilingFormSelection({
  filings,
  isCustomFilter,
  selectedCustomForms,
}) {
  const loadedCustomFormOptions = useMemo(() => {
    const seen = new Set();
    const options = [];
    for (const filing of filings) {
      const value = filing.formTypeKey;
      if (!value || seen.has(value)) {
        continue;
      }
      seen.add(value);
      options.push({
        value,
        label: filing.formType || value || '—',
      });
    }
    return options.sort((a, b) => compareStringValues(a.value, b.value));
  }, [filings]);

  const customFormOptions = useMemo(() => {
    const optionsByValue = new Map(
      loadedCustomFormOptions.map((option) => [option.value, option])
    );
    normalizeTrimmedStringList(selectedCustomForms).forEach((value) => {
      if (!optionsByValue.has(value)) {
        optionsByValue.set(value, { value, label: value });
      }
    });
    return [...optionsByValue.values()]
      .sort((a, b) => compareStringValues(a.value, b.value));
  }, [loadedCustomFormOptions, selectedCustomForms]);

  const loadedCustomFormSet = useMemo(
    () => new Set(loadedCustomFormOptions.map((option) => option.value)),
    [loadedCustomFormOptions]
  );

  const defaultCustomFormSet = useMemo(() => {
    const categoryPredicates = [];
    for (const option of SEC_FILING_FILTER_OPTIONS) {
      if (DEFAULT_CUSTOM_FILTER_CATEGORY_VALUES.has(option.value)) {
        categoryPredicates.push(option.predicate);
      }
    }

    const defaultFormKeys = new Set();
    filings.forEach((filing) => {
      const inDefaultCategory = categoryPredicates.some((predicate) => predicate(filing));
      if (inDefaultCategory && filing?.formTypeKey) {
        defaultFormKeys.add(filing.formTypeKey);
      }
    });
    return defaultFormKeys;
  }, [filings]);

  const selectedCustomFormSet = useMemo(
    () =>
      new Set(normalizeTrimmedStringList(selectedCustomForms)),
    [selectedCustomForms]
  );

  const customFilterMenuLabel = useMemo(() => {
    if (!isCustomFilter) {
      return '';
    }

    const total = customFormOptions.length;
    if (!total) {
      return 'Forms: none available';
    }

    if (selectedCustomForms.length === 0) {
      return `Forms: 0/${total}`;
    }

    if (selectedCustomForms.length === total) {
      return `Forms: All (${total})`;
    }

    return `Forms: ${selectedCustomForms.length}/${total}`;
  }, [isCustomFilter, customFormOptions.length, selectedCustomForms.length]);

  return {
    customFilterMenuLabel,
    customFormOptions,
    defaultCustomFormSet,
    loadedCustomFormSet,
    selectedCustomFormSet,
  };
}

function useStockSecFilingsController(preIpoEntity = null) {
  const { ticker: routeTicker } = useParams();
  const ticker = routeTicker || '';
  const entityCik = preIpoEntity?.cik || null;
  const location = useLocation();
  const requestedFilter = useMemo(
    () => getSecFilingFilterFromSearch(location.search),
    [location.search]
  );
  const {
    isActive: isReviewModeActive,
    sinceDate,
    clear: clearReviewMode,
  } = useStockReviewMode();
  const {
    isAuthenticated,
    isLoading: isAuthLoading,
    getAccessTokenSilently,
    loginWithRedirect,
  } = useAuth0();
  const [
    {
      isFiltering,
      selectedFilter,
      selectedMasterTags,
      selectedCustomForms,
      activeSavedCustomFilterId,
      currentReportsQuery,
      sortState,
    },
    dispatchFilingsFilter,
  ] = useReducer(filingsFilterReducer, requestedFilter, getInitialFilingsFilterState);
  const appliedRequestedFilterRef = React.useRef(requestedFilter);
  const preIpoFilterInitializedRef = React.useRef(false);
  const registrationOnlyTickerRef = React.useRef(null);

  useEffect(() => {
    if (!entityCik || preIpoFilterInitializedRef.current) return;
    preIpoFilterInitializedRef.current = true;
    dispatchFilingsFilter({
      type: 'APPLY_FILTER_SELECT',
      filterValue: 'ipo-registration',
    });
  }, [entityCik]);

  useEffect(() => {
    if (requestedFilter === appliedRequestedFilterRef.current) return;
    appliedRequestedFilterRef.current = requestedFilter;
    if (!requestedFilter) return;
    dispatchFilingsFilter({
      type: 'APPLY_FILTER_SELECT',
      filterValue: requestedFilter,
    });
  }, [requestedFilter]);

  const setSelectedMasterTags = useCallback((value) => {
    dispatchFilingsFilter({ type: 'SET_SELECTED_MASTER_TAGS', value });
  }, []);

  const setSelectedCustomForms = useCallback((value) => {
    dispatchFilingsFilter({ type: 'SET_SELECTED_CUSTOM_FORMS', value });
  }, []);
  // Tracks whether the latest selectedCustomForms change came from an explicit
  // user action (toggle/select-all/clear-all) rather than ticker-specific pruning.
  const userEditedFormsRef = React.useRef(false);
  const apiBaseUrl = useMemo(() => {
    const raw = process.env.REACT_APP_API_URL || '';
    return raw.endsWith('/') ? raw.slice(0, -1) : raw;
  }, []);
  const {
    retrySummaryErrors,
    enrichForm4,
    error,
    filings,
    hasMore,
    isFetching,
    isLoadingAll,
    isLoadingMore,
    loadAll,
    loadMore,
    totalCount,
  } = useSecFilingsData(ticker, apiBaseUrl, { entityCik });

  useEffect(() => {
    if (
      entityCik
      || !ticker
      || hasMore
      || filings.length === 0
      || registrationOnlyTickerRef.current === ticker
    ) {
      return;
    }

    const registrationFilter = getSecFilingFilterOption('ipo-registration');
    if (!filings.every((filing) => registrationFilter.predicate(filing))) {
      return;
    }

    registrationOnlyTickerRef.current = ticker;
    dispatchFilingsFilter({
      type: 'APPLY_FILTER_SELECT',
      filterValue: 'ipo-registration',
    });
  }, [entityCik, filings, hasMore, ticker]);

  const reviewFilings = useMemo(
    () => isReviewModeActive
      ? filings.filter((filing) => isAfterDate(filing?.filingDate, sinceDate))
      : filings,
    [filings, isReviewModeActive, sinceDate]
  );
  const isLoading = isFetching || isFiltering;
  const {
    savedCustomFilterLimit,
    savedCustomFilters,
    removeSavedCustomFilter,
    upsertSavedCustomFilter,
  } = useSavedSecFilingFiltersData(apiBaseUrl, getAccessTokenSilently, isAuthenticated);
  const {
    deleteNotableDate,
    error: notableDatesError,
    needsReauth: notableDatesNeedReauth,
    notableDateByFilingKey,
    saveFilingDate,
  } = useNotableDates({
    apiBaseUrl,
    ticker,
    getAccessTokenSilently,
    isAuthenticated,
  });
  const [savingNotableDateKey, setSavingNotableDateKey] = useState(null);
  const [notableDateActionError, setNotableDateActionError] = useState('');

  const handleExpandForm4 = useCallback((filing) => {
    if (isForm4Filing(filing)) {
      enrichForm4(filing);
    }
  }, [enrichForm4]);

  const currentReturnTo = `${location.pathname}${location.search}${location.hash}`;
  const refreshLogin = React.useCallback(() => loginWithRedirect(
    getLoginRedirectOptions(currentReturnTo, {
      authorizationParams: { prompt: 'consent' },
    })
  ), [currentReturnTo, loginWithRedirect]);
  const activeFilter = getSecFilingFilterOption(selectedFilter);
  const isCurrentReportsFilter = activeFilter?.value === 'current-reports';
  const isCustomFilter = activeFilter?.value === 'custom';

  const {
    customFilterMenuLabel,
    customFormOptions,
    defaultCustomFormSet,
    loadedCustomFormSet,
    selectedCustomFormSet,
  } = useCustomFilingFormSelection({
    filings: reviewFilings,
    isCustomFilter,
    selectedCustomForms,
  });

  const markUserEditedForms = React.useCallback(() => {
    userEditedFormsRef.current = true;
  }, []);

  const setActiveSavedCustomFilterId = useCallback((value) => {
    dispatchFilingsFilter({ type: 'SET_ACTIVE_SAVED_CUSTOM_FILTER_ID', value });
  }, []);

  useEffect(() => {
    userEditedFormsRef.current = false;
  }, [entityCik, ticker]);

  useEffect(() => {
    if (!isCustomFilter) {
      return;
    }

    dispatchFilingsFilter({
      type: 'SET_SELECTED_CUSTOM_FORMS',
      value: (prev) => {
        if (prev.length > 0) {
          return prev;
        }
        // Respect explicit "Clear all" — don't repopulate defaults while empty.
        if (userEditedFormsRef.current) {
          return prev;
        }
        if (defaultCustomFormSet.size === 0) {
          return prev;
        }

        const next = [...defaultCustomFormSet];
        if (
          prev.length === next.length
          && prev.every((formType, index) => formType === next[index])
        ) {
          return prev;
        }
        return next;
      },
    });
  }, [defaultCustomFormSet, isCustomFilter]);

  useEffect(() => {
    if (!isCustomFilter) {
      return;
    }
    // The available form list is incomplete while filings are paginated. Keep
    // saved selections that are absent from the first page so they can match
    // older filings after the user loads the remaining pages.
    if (loadedCustomFormSet.size === 0 || hasMore) {
      return;
    }

    dispatchFilingsFilter({
      type: 'SET_SELECTED_CUSTOM_FORMS',
      value: (prev) => {
        const pruned = prev.filter((formType) =>
          loadedCustomFormSet.has(String(formType || '').trim())
        );
        if (
          pruned.length === prev.length
          && pruned.every((formType, index) => formType === prev[index])
        ) {
          return prev;
        }
        return pruned;
      },
    });
  }, [hasMore, isCustomFilter, loadedCustomFormSet]);

  const {
    activeSavedCustomFilter,
    clearActiveCustomFilter,
    customFilterNameInput,
    deleteActiveCustomFilter,
    isSavingFilter,
    saveCurrentCustomFilter,
    saveFilterError,
    scheduleSaveActiveCustomFilterForms,
    selectSavedCustomFilter,
    updateCustomFilterName,
  } = useSavedCustomFilingFilters({
    activeSavedCustomFilterId,
    apiBaseUrl,
    getAccessTokenSilently,
    isAuthenticated,
    onActiveSavedCustomFilterIdChange: setActiveSavedCustomFilterId,
    removeSavedCustomFilter,
    savedCustomFilterLimit,
    savedCustomFilters,
    upsertSavedCustomFilter,
  });

  const handleSelectAllCustomForms = React.useCallback(() => {
    const nextForms = customFormOptions.map((option) => option.value);
    markUserEditedForms();
    setSelectedCustomForms(nextForms);
    scheduleSaveActiveCustomFilterForms(nextForms);
  }, [
    customFormOptions,
    markUserEditedForms,
    scheduleSaveActiveCustomFilterForms,
    setSelectedCustomForms,
  ]);

  const handleSelectNoCustomForms = React.useCallback(() => {
    markUserEditedForms();
    setSelectedCustomForms([]);
    scheduleSaveActiveCustomFilterForms([]);
  }, [markUserEditedForms, scheduleSaveActiveCustomFilterForms, setSelectedCustomForms]);

  const handleSelectCustomForm = React.useCallback((optionValue) => {
    const exists = selectedCustomForms.includes(optionValue);
    const nextForms = exists
      ? selectedCustomForms.filter((item) => item !== optionValue)
      : [...selectedCustomForms, optionValue];

    markUserEditedForms();
    setSelectedCustomForms(nextForms);
    scheduleSaveActiveCustomFilterForms(nextForms);
  }, [
    markUserEditedForms,
    scheduleSaveActiveCustomFilterForms,
    selectedCustomForms,
    setSelectedCustomForms,
  ]);

  const handleDeleteActiveCustomFilter = React.useCallback(async () => {
    const result = await deleteActiveCustomFilter();
    if (result === 'auth-required') {
      await loginWithRedirect({
        appState: { returnTo: currentReturnTo },
        authorizationParams: {
          redirect_uri: window.location.origin,
        },
      });
      return;
    }

    if (result) {
      dispatchFilingsFilter({ type: 'APPLY_FILTER_SELECT', filterValue: 'exclude-insider' });
    }
  }, [currentReturnTo, deleteActiveCustomFilter, loginWithRedirect]);

  const handleSaveCurrentCustomFilter = React.useCallback(async () => {
    const result = await saveCurrentCustomFilter(selectedCustomForms);
    if (result === 'auth-required') {
      await loginWithRedirect({
        appState: { returnTo: currentReturnTo },
        authorizationParams: {
          redirect_uri: window.location.origin,
        },
      });
    }
  }, [currentReturnTo, loginWithRedirect, saveCurrentCustomFilter, selectedCustomForms]);

  const handleSaveNotableDate = React.useCallback(async (filing) => {
    const key = getNotableDateKeyForFiling(filing);
    setNotableDateActionError('');
    setSavingNotableDateKey(key);
    try {
      const result = await saveFilingDate(filing);
      if (result === 'auth-required') {
        await loginWithRedirect({
          appState: { returnTo: currentReturnTo },
          authorizationParams: {
            redirect_uri: window.location.origin,
          },
        });
      }
    } catch (err) {
      if (isRecoverableAuthError(err)) {
        await refreshLogin();
        return;
      }
      setNotableDateActionError(
        err?.response?.data?.error || err?.message || 'Could not save this notable date.'
      );
    } finally {
      setSavingNotableDateKey(null);
    }
  }, [currentReturnTo, loginWithRedirect, refreshLogin, saveFilingDate]);

  const handleDeleteNotableDate = React.useCallback(async (notableDate, filing) => {
    const key = getNotableDateKeyForFiling(filing);
    setNotableDateActionError('');
    setSavingNotableDateKey(key);
    try {
      const result = await deleteNotableDate(notableDate);
      if (result === 'auth-required') {
        await loginWithRedirect({
          appState: { returnTo: currentReturnTo },
          authorizationParams: {
            redirect_uri: window.location.origin,
          },
        });
      }
    } catch (err) {
      if (isRecoverableAuthError(err)) {
        await refreshLogin();
        return;
      }
      setNotableDateActionError(
        err?.response?.data?.error || err?.message || 'Could not delete this notable date.'
      );
    } finally {
      setSavingNotableDateKey(null);
    }
  }, [currentReturnTo, deleteNotableDate, loginWithRedirect, refreshLogin]);

  // Persist generic Custom form selections so they survive a page reload.
  // Saved-filter persistence (with savedFilterId) is handled separately by
  // useSavedCustomFilingFilters, so skip while a saved filter is active or
  // still pending restore to avoid clobbering its stored savedFilterId.
  useEffect(() => {
    if (!isCustomFilter || activeSavedCustomFilterId || activeSavedCustomFilter) return;
    try {
      const raw = localStorage.getItem(FILINGS_FILTER_STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed?.savedFilterId) return;
      }
      localStorage.setItem(
        FILINGS_FILTER_STORAGE_KEY,
        JSON.stringify({ filter: 'custom', forms: selectedCustomForms })
      );
    } catch {
      // localStorage unavailable — proceed without persisting
    }
  }, [isCustomFilter, activeSavedCustomFilterId, activeSavedCustomFilter, selectedCustomForms]);
  const selectedMasterTagSet = useMemo(
    () =>
      new Set(normalizeTrimmedStringList(selectedMasterTags)),
    [selectedMasterTags]
  );

  const currentReportsQueryNormalised = useMemo(
    () => String(currentReportsQuery || '').trim().toLowerCase(),
    [currentReportsQuery]
  );
  const currentReportsQueryTrimmed = useMemo(
    () => String(currentReportsQuery || '').trim(),
    [currentReportsQuery]
  );
  const showMasterTagControls = isCurrentReportsFilter || isCustomFilter;
  const isSearchActive = Boolean(currentReportsQueryNormalised);

  const masterTagMenuLabel = useMemo(() => {
    if (!showMasterTagControls) {
      return '';
    }

    if (selectedMasterTags.length === 0) {
      return 'Master tags: All';
    }

    if (selectedMasterTags.length === MASTER_TAG_KEYS.length) {
      return 'Master tags: All';
    }

    return `Master tags: ${selectedMasterTags.length}/${MASTER_TAG_KEYS.length}`;
  }, [showMasterTagControls, selectedMasterTags.length]);

  const toggleMasterTag = React.useCallback((tagKey) => {
    const safeKey = String(tagKey || '').trim();
    if (!safeKey) return;

    dispatchFilingsFilter({
      type: 'SET_SELECTED_MASTER_TAGS',
      value: (prev) => {
        const prevKeys = Array.isArray(prev) ? prev : [];
        const isSelected = prevKeys.includes(safeKey);
        return isSelected ? prevKeys.filter((key) => key !== safeKey) : [...prevKeys, safeKey];
      },
    });
  }, []);

  const handleFilterSelect = React.useCallback((option) => {
    const leavingSavedCustomFilter =
      option.value === 'custom' && activeSavedCustomFilter != null;
    if (option.value === selectedFilter && !leavingSavedCustomFilter) {
      return;
    }

    userEditedFormsRef.current = false;

    try {
      localStorage.setItem(FILINGS_FILTER_STORAGE_KEY, JSON.stringify({ filter: option.value }));
    } catch {
      // localStorage unavailable — proceed without persisting
    }

    dispatchFilingsFilter({ type: 'APPLY_FILTER_SELECT', filterValue: option.value });
    clearActiveCustomFilter();
  }, [selectedFilter, activeSavedCustomFilter, clearActiveCustomFilter]);

  const handleSavedFilterSelect = React.useCallback((saved) => {
    const savedForms = normalizeTrimmedStringList(saved.forms);
    if (savedForms.length === 0) {
      return;
    }

    userEditedFormsRef.current = false;

    try {
      localStorage.setItem(FILINGS_FILTER_STORAGE_KEY, JSON.stringify({
        filter: 'custom',
        forms: savedForms,
        savedFilterId: saved.id,
      }));
    } catch {
      // localStorage unavailable — proceed without persisting
    }

    dispatchFilingsFilter({
      type: 'APPLY_SAVED_FILTER_SELECT',
      savedFilterId: saved.id,
      forms: savedForms,
    });
    selectSavedCustomFilter(saved.id);
  }, [selectSavedCustomFilter]);

  const handleCustomFilterNameChange = React.useCallback((value) => {
    updateCustomFilterName(value);
  }, [updateCustomFilterName]);

  const handleCurrentReportsQueryChange = React.useCallback((value) => {
    dispatchFilingsFilter({ type: 'SET_CURRENT_REPORTS_QUERY', value });
  }, []);

  const handleClearCurrentReportsQuery = React.useCallback(() => {
    dispatchFilingsFilter({ type: 'SET_CURRENT_REPORTS_QUERY', value: '' });
  }, []);

  const {
    activeFilterTotalCount,
    activeSort,
    filteredFilings,
    sortedFilings,
  } = useFilteredSecFilings({
    activeFilter,
    currentReportsQueryNormalised,
    filings: reviewFilings,
    isCurrentReportsFilter,
    isCustomFilter,
    selectedCustomFormSet,
    selectedMasterTagSet,
    sortState,
  });

  const tableColumns = useMemo(() => [
    ...SEC_FILING_COLUMNS,
    ...(!preIpoEntity ? [{
      key: 'notableDate',
      label: 'Add to timeline',
      sortable: false,
      render: (filing) => {
        const key = getNotableDateKeyForFiling(filing);
        const notableDate = notableDateByFilingKey.get(key);
        return (
          <FilingNotableDateButton
            filing={filing}
            isSaving={savingNotableDateKey === key}
            notableDate={notableDate}
            onDeleteNotableDate={handleDeleteNotableDate}
            onSaveNotableDate={handleSaveNotableDate}
            stockPriceHref={getStockPriceTimelineHref(ticker)}
          />
        );
      },
    }] : []),
  ], [
    handleDeleteNotableDate,
    handleSaveNotableDate,
    notableDateByFilingKey,
    savingNotableDateKey,
    ticker,
    preIpoEntity,
  ]);

  const cycleSortForColumn = React.useCallback((columnKey, isSortable) => {
    if (!isSortable) {
      return;
    }

    dispatchFilingsFilter({
      type: 'SET_SORT_STATE',
      value: (prev) => {
        if (prev.isDefault && columnKey === DEFAULT_SEC_FILING_SORT.column) {
          return { column: columnKey, direction: 'asc', isDefault: false };
        }

        if (prev.column !== columnKey) {
          return { column: columnKey, direction: 'asc', isDefault: false };
        }

        if (prev.direction === 'asc') {
          return { column: columnKey, direction: 'desc', isDefault: false };
        }

        return { ...DEFAULT_SEC_FILING_SORT, isDefault: true };
      },
    });
  }, []);

  useEffect(() => {
    if (!isFiltering) {
      return;
    }

    dispatchFilingsFilter({ type: 'SET_IS_FILTERING', value: false });
  }, [filteredFilings, isFiltering]);

  return {
    activeFilter,
    activeFilterTotalCount,
    activeSavedCustomFilter,
    activeSort,
    customFilterMenuLabel,
    customFilterNameInput,
    customFormOptions,
    currentReportsQuery,
    currentReportsQueryTrimmed,
    error,
    notableDateError: notableDateActionError || notableDatesError,
    notableDatesNeedReauth,
    refreshLogin,
    filings,
    handleClearCurrentReportsQuery,
    handleCurrentReportsQueryChange,
    handleCustomFilterNameChange,
    handleDeleteActiveCustomFilter,
    handleFilterSelect,
    handleExpandForm4,
    handleSaveCurrentCustomFilter,
    handleSavedFilterSelect,
    handleSelectAllCustomForms,
    handleSelectCustomForm,
    handleSelectNoCustomForms,
    isAuthLoading,
    isAuthenticated,
    isCustomFilter,
    hasMore,
    isLoading,
    isLoadingAll,
    isLoadingMore,
    isSavingFilter,
    isSearchActive,
    reviewSinceDate: isReviewModeActive ? sinceDate : null,
    reviewFilingsCount: reviewFilings.length,
    clearReviewMode,
    masterTagMenuLabel,
    savedCustomFilterLimit,
    savedCustomFilters,
    saveFilterError,
    selectedCustomForms,
    selectedCustomFormSet,
    selectedMasterTagSet,
    setSelectedMasterTags,
    showMasterTagControls,
    sortedFilings,
    tableColumns,
    ticker,
    preIpoEntity,
    totalCount,
    loadAll,
    loadMore,
    retrySummaryErrors,
    toggleMasterTag,
    cycleSortForColumn,
    hasFilings: sortedFilings.length > 0,
  };
}

function StockSecFilings({ preIpoEntity = null }) {
  const view = useStockSecFilingsController(preIpoEntity);
  const summaryProgress = view.filings.filter((filing) => /^8-K(?:\/A)?$/.test(filing.formTypeKey))
    .reduce((counts, filing) => {
      counts.total += 1;
      if (filing.ai?.status === 'complete') counts.complete += 1;
      else if (filing.ai?.status === 'error') counts.failed += 1;
      else if (filing.ai?.status === 'pending') counts.pending += 1;
      else counts.queued += 1;
      return counts;
    }, { total: 0, complete: 0, pending: 0, queued: 0, failed: 0 });

  return (
    <Box p={4}>
      <VStack align="stretch" spacing={2}>
        <Box>
          <Heading size="lg" mb={2}>
            {view.preIpoEntity?.company_name
              ? `${view.preIpoEntity.company_name} SEC Filings`
              : `${view.ticker?.toUpperCase()} SEC Filings`}
          </Heading>
          <Text color="gray.600">
            {view.preIpoEntity
              ? 'Pre-IPO registration history from the SEC EDGAR system.'
              : 'Explore recent filings from the SEC EDGAR system.'}
          </Text>
        </Box>

        {view.preIpoEntity && (
          <Alert.Root status={view.preIpoEntity.is_pre_ipo ? 'info' : 'success'}>
            <Alert.Indicator />
            <Alert.Content>
              {view.preIpoEntity.is_pre_ipo ? (
                `Pre-IPO${view.preIpoEntity.proposed_ticker
                  ? ` · Proposed ${view.preIpoEntity.proposed_exchange || 'exchange'} ticker ${view.preIpoEntity.proposed_ticker}`
                  : ''} · CIK ${view.preIpoEntity.cik}`
              ) : (
                <Text>
                  This company now has an SEC ticker mapping. CIK {view.preIpoEntity.cik}
                  {view.preIpoEntity.sec_tickers?.[0] ? (
                    <>
                      {' · '}
                      <Link asChild color="blue.600" fontWeight="semibold">
                        <RouterLink to={`/stock/${view.preIpoEntity.sec_tickers[0].toLowerCase()}/sec-filings`}>
                          Open {view.preIpoEntity.sec_tickers[0]}
                        </RouterLink>
                      </Link>
                    </>
                  ) : null}
                </Text>
              )}
            </Alert.Content>
          </Alert.Root>
        )}

        {view.error && (
          <Alert.Root status="error">
            <Alert.Indicator />
            <Alert.Content>
              {view.error}
            </Alert.Content>
          </Alert.Root>
        )}
        {view.notableDateError && (
          <Alert.Root status="warning">
            <Alert.Indicator />
            <Alert.Content>
              {view.notableDateError}
            </Alert.Content>
          </Alert.Root>
        )}
        {view.notableDatesNeedReauth && (
          <Alert.Root status="warning">
            <Alert.Indicator />
            <Alert.Content>
              <Text>Your login session needs a quick refresh to manage timeline dates.</Text>
              <Button size="sm" variant="outline" mt={2} onClick={view.refreshLogin}>
                Sign in again
              </Button>
            </Alert.Content>
          </Alert.Root>
        )}

        <FilingsControls
          activeFilter={view.activeFilter}
          activeFilterTotalCount={view.activeFilterTotalCount}
          activeSavedCustomFilter={view.activeSavedCustomFilter}
          customFilterMenuLabel={view.customFilterMenuLabel}
          customFilterNameInput={view.customFilterNameInput}
          customFormOptions={view.customFormOptions}
          currentReportsQuery={view.currentReportsQuery}
          currentReportsQueryTrimmed={view.currentReportsQueryTrimmed}
          deleteActiveCustomFilter={view.handleDeleteActiveCustomFilter}
          isAuthLoading={view.isAuthLoading}
          isAuthenticated={view.isAuthenticated}
          isCustomFilter={view.isCustomFilter}
          isLoading={view.isLoading}
          isSavingFilter={view.isSavingFilter}
          isSearchActive={view.isSearchActive}
          masterTagMenuLabel={view.masterTagMenuLabel}
          onClearCurrentReportsQuery={view.handleClearCurrentReportsQuery}
          onCurrentReportsQueryChange={view.handleCurrentReportsQueryChange}
          onCustomFilterNameChange={view.handleCustomFilterNameChange}
          onFilterSelect={view.handleFilterSelect}
          onSavedFilterSelect={view.handleSavedFilterSelect}
          onSelectAllCustomForms={view.handleSelectAllCustomForms}
          onSelectCustomForm={view.handleSelectCustomForm}
          onSelectNoCustomForms={view.handleSelectNoCustomForms}
          saveCurrentCustomFilter={view.handleSaveCurrentCustomFilter}
          savedCustomFilterLimit={view.savedCustomFilterLimit}
          savedCustomFilters={view.savedCustomFilters}
          saveFilterError={view.saveFilterError}
          selectedCustomForms={view.selectedCustomForms}
          selectedCustomFormSet={view.selectedCustomFormSet}
          selectedMasterTagSet={view.selectedMasterTagSet}
          setSelectedMasterTags={view.setSelectedMasterTags}
          showMasterTagControls={view.showMasterTagControls}
          sortedFilings={view.sortedFilings}
          toggleMasterTag={view.toggleMasterTag}
        />

        {summaryProgress.total > 0 && (
          <Box borderWidth="1px" borderRadius="md" p={3} bg="blue.50">
            <Text role="status" fontSize="sm">
              8-K summaries: {summaryProgress.complete} of {summaryProgress.total} ready
              {summaryProgress.pending > 0 ? ` · ${summaryProgress.pending} generating` : ''}
              {summaryProgress.queued > 0 ? ` · ${summaryProgress.queued} queued` : ''}
              {summaryProgress.failed > 0 ? ` · ${summaryProgress.failed} unavailable` : ''}
            </Text>
            {(summaryProgress.pending > 0 || summaryProgress.queued > 0) && (
              <Text fontSize="sm" color="gray.600">
                Summaries continue while this page is open. You can search, filter, and browse all loaded filings now.
              </Text>
            )}
            {summaryProgress.failed > 0 && (
              <Button size="sm" variant="outline" mt={2} onClick={view.retrySummaryErrors}>
                Retry unavailable summaries
              </Button>
            )}
          </Box>
        )}
        <FilingsTable
          activeSort={view.activeSort}
          columns={view.tableColumns}
          cycleSortForColumn={view.cycleSortForColumn}
          filings={view.filings}
          hasFilings={view.hasFilings}
          hasMore={view.hasMore}
          isLoading={view.isLoading}
          isLoadingAll={view.isLoadingAll}
          isLoadingMore={view.isLoadingMore}
          onLoadAll={view.loadAll}
          onLoadMore={view.loadMore}
          totalCount={view.totalCount}
          onClearReviewMode={view.clearReviewMode}
          reviewFilingsCount={view.reviewFilingsCount}
          reviewSinceDate={view.reviewSinceDate}
          sortedFilings={view.sortedFilings}
          isRowExpandable={isForm4Filing}
          onExpandRow={view.handleExpandForm4}
          renderExpandedRow={(filing) => <Form4ExpandedDetails filing={filing} />}
        />
      </VStack>
    </Box>
  );
}

export default StockSecFilings;
